"use client";

import { Shield, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { setAmplitudeConsent } from "@/lib/analytics/amplitude";
import { reportWarning } from "@/lib/observability/report-error";
import { cn } from "@/lib/utils";

const CONSENT_KEY = "vidseeds_cookie_consent";

type ConsentChoice = "accepted" | "rejected" | "customized";

interface StoredConsent {
  choice: ConsentChoice;
  timestamp: string;
}

function getStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch (err) {
    console.error("[cookie-consent] failed to read stored consent:", err);
    reportWarning(err, {
      source: "components.CookieConsentBanner",
      feature: "consent",
      action: "readStoredConsent",
    });
    return null;
  }
}

function storeConsent(choice: ConsentChoice) {
  try {
    localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ choice, timestamp: new Date().toISOString() }),
    );
  } catch (err) {
    console.error("[cookie-consent] failed to store consent:", err);
    reportWarning(err, {
      source: "components.CookieConsentBanner",
      feature: "consent",
      action: "storeConsent",
    });
  }
}

export function CookieConsentBanner() {
  const { t } = useJsonTranslation("CookieConsent");
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Show banner only if no prior consent stored
    const consent = getStoredConsent();
    if (!consent) {
      // Small delay so it doesn't flash on initial load
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    storeConsent("accepted");
    setAmplitudeConsent(true);
    setVisible(false);
  }, []);

  const handleRejectNonEssential = useCallback(() => {
    storeConsent("rejected");
    setAmplitudeConsent(false);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent("customized");
    // "Customized" with default-checked analytics toggle = accepted
    setAmplitudeConsent(true);
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-[9999] p-4 sm:p-6",
        "animate-in slide-in-from-bottom-4 fade-in duration-500",
      )}
    >
      <div className="from-surface-raised/95 to-surface-raised/85 mx-auto max-w-2xl overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b shadow-2xl backdrop-blur-xl">
        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-5 pb-3 sm:px-6">
          <div className="from-leaf-500/20 to-leaf-500/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ring-1 ring-white/[0.08]">
            <Shield className="text-leaf-400 h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-foreground text-sm font-semibold">
              {t("title")}
            </h3>
            <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
              {t("description")}
            </p>
          </div>
          <button
            type="button"
            onClick={handleAcceptAll}
            className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 rounded-lg p-1.5 transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Preferences panel */}
        {showPreferences && (
          <div className="space-y-2.5 border-t border-white/[0.06] px-5 pt-4 pb-2 sm:px-6">
            <PreferenceToggle
              label={t("essential")}
              description={t("essentialDesc")}
              checked
              disabled
            />
            <PreferenceToggle
              label={t("analytics")}
              description={t("analyticsDesc")}
              defaultChecked
            />
            <PreferenceToggle
              label={t("performance")}
              description={t("performanceDesc")}
              defaultChecked
            />
            <PreferenceToggle
              label={t("marketing")}
              description={t("marketingDesc")}
              defaultChecked
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 px-5 pt-2 pb-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <button
            type="button"
            onClick={() => setShowPreferences((prev) => !prev)}
            className="text-muted-foreground hover:text-foreground text-xs font-medium underline-offset-2 transition-colors hover:underline"
          >
            {showPreferences ? t("hidePreferences") : t("managePreferences")}
          </button>
          <div className="flex gap-2 max-sm:flex-col">
            {showPreferences ? (
              <button
                type="button"
                onClick={handleSavePreferences}
                className="bg-leaf-600 hover:bg-leaf-700 rounded-lg px-5 py-2 text-xs font-semibold text-white transition-all active:scale-[0.97]"
              >
                {t("savePreferences")}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleRejectNonEssential}
                  className="border-border rounded-lg border px-4 py-2 text-xs font-medium transition-all hover:bg-white/[0.04] active:scale-[0.97]"
                >
                  {t("rejectNonEssential")}
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  className="bg-leaf-600 hover:bg-leaf-700 rounded-lg px-5 py-2 text-xs font-semibold text-white transition-all active:scale-[0.97]"
                >
                  {t("acceptAll")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PreferenceToggle({
  label,
  description,
  checked,
  defaultChecked,
  disabled,
}: {
  label: string;
  description: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex items-center justify-between gap-3 rounded-lg px-3 py-2.5",
        disabled ? "opacity-70" : "cursor-pointer hover:bg-white/[0.03]",
      )}
    >
      <div className="min-w-0">
        <span className="text-foreground text-xs font-medium">{label}</span>
        <p className="text-muted-foreground/60 mt-0.5 text-[11px] leading-snug">
          {description}
        </p>
      </div>
      <div className="relative shrink-0">
        <input
          type="checkbox"
          className="peer sr-only"
          checked={checked}
          defaultChecked={defaultChecked ?? true}
          disabled={disabled}
        />
        <div className="peer-checked:bg-leaf-500 bg-muted h-5 w-9 rounded-full transition-colors peer-disabled:opacity-50" />
        <div className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
      </div>
    </label>
  );
}
